import { useState } from 'react'
import { type IsdDistrictProgramEnhancement } from '../../../../../models/IsdDistrictProgramEnhancement'
import { isValidPositiveNumber } from '../../../../../utils/isValidNumber'
import { ProgramEnhancementCurrentProgress } from '../../../../../models/ProgramEnhancementCurrentProgress'
import { toast } from 'react-toastify'
import { type UpdateIsdDistrictProgramEnhancementFormValues } from './IsdDistrictProgramEnhancementModal'
import { UpdateIsdDistrictProgramEnhancement } from '../../../../../services/AnnualPlanService'
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { Modal } from '../../../../customComponents/Modal'

export interface ProgramEnhancementTrackingFormValues {
  currentProgress: string[] | null
  actualAmount: string | number | null
}

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
  programEnhancement: IsdDistrictProgramEnhancement
  implementationPhase: boolean
}

export const ProgramEnhancementTrackingModal = (props: Props): JSX.Element => {
  const [formValues, setFormValues] = useState<ProgramEnhancementTrackingFormValues>({
    actualAmount: props.programEnhancement?.actualBudgetAmount ?? null,
    currentProgress: props.programEnhancement?.currentProgress ?? null
  })
  const [submitted, setSubmitted] = useState<boolean>(false)

  const formValidators: Record<keyof ProgramEnhancementTrackingFormValues, { required: boolean, isValid: boolean }> = {
    currentProgress: { required: true, isValid: formValues.currentProgress !== null && Array.isArray(formValues.currentProgress) && formValues.currentProgress.length > 0 && formValues.currentProgress?.every(cp => Object.values(ProgramEnhancementCurrentProgress).includes(cp)) },
    actualAmount: { required: false, isValid: formValues.actualAmount == null || formValues.actualAmount === '' || isValidPositiveNumber(formValues.actualAmount) }
  }
  const hasInvalidFormValues = Object.values(formValidators).some(v => !v.isValid)

  const handleFieldUpdate = (e: { target: { name: string, value: string } }): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleSubmit = async (): Promise<void> => {
    if (!props.programEnhancement.isUpdate) {
      toast.error('This enhancement plan has no updates to track.')
      props.onClose()
      return
    }

    setSubmitted(true)
    if (hasInvalidFormValues) {
      toast.error('Please fill out required fields')
      return
    }

    const updatedProgramEnhancement: UpdateIsdDistrictProgramEnhancementFormValues = {
      id: props.programEnhancement.id,
      priorityId: props.programEnhancement.priorityId,
      organizationType: props.programEnhancement.organizationType,
      organizationId: props.programEnhancement.organization.id,
      organizationContactName: props.programEnhancement.organizationContactName,
      organizationContactEmail: props.programEnhancement.organizationContactEmail,
      actions: props.programEnhancement.actions,
      resources: props.programEnhancement.resources,
      budgetRequest: props.programEnhancement.budgetRequest,
      isImplementationPhase: props.implementationPhase,
      isUpdate: props.programEnhancement.isUpdate,
      actualAmount: isValidPositiveNumber(formValues.actualAmount) ? Number(formValues.actualAmount) : null,
      currentProgress: Array.isArray(formValues.currentProgress) && formValues.currentProgress.length > 0 ? formValues.currentProgress : null
    }

    const { success, errors } = await UpdateIsdDistrictProgramEnhancement(updatedProgramEnhancement)
    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    toast.success('Program Enhancement Plan Data Tracked')
    props.onSaved()
    props.onClose()
  }

  const handleCurrentProgressChange = (progress: string, checked: boolean): void => {
    let updatedProgress: string[] = formValues.currentProgress ?? []
    if (checked) {
      if (!updatedProgress.includes(progress)) {
        updatedProgress = [...updatedProgress, progress]
      }
    } else {
      updatedProgress = updatedProgress.filter(p => p !== progress)
    }
    setFormValues({ ...formValues, currentProgress: updatedProgress.length > 0 ? updatedProgress : null })
  }

  const renderTrackingFields = (): JSX.Element | null => {
    if (!props.programEnhancement.isUpdate) return null

    return (
      <>
        <Typography variant='h5'>Progress</Typography>

        <FormControl
          data-testid='currentProgress'
          required={formValidators.currentProgress.required}
          error={submitted && !formValidators.currentProgress.isValid}
          component="fieldset"
          variant="filled"
        >
          <FormLabel component="legend">
            Please select your current progress.
            <Typography component='span' variant='caption' fontStyle='italic' color='var(--grey-500)'> Select all that apply</Typography>
          </FormLabel>
          <FormGroup>
            {Object.values(ProgramEnhancementCurrentProgress).map(cp => (
              <FormControlLabel
                key={cp}
                label={cp}
                control={
                  <Checkbox
                    data-testid={`currentProgress-${cp}`}
                    checked={formValues.currentProgress?.includes(cp) ?? false}
                    onChange={(_, checked) => { handleCurrentProgressChange(cp, checked) }}
                    name='currentProgress'
                  />
                }
              />
            ))}
          </FormGroup>
        </FormControl>

        <Typography variant='h5'>Budget</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography variant='body1'>
            How much funding was actually used?
            <Typography component='span' variant='caption' fontStyle='italic' color='var(--grey-500)'> Optional</Typography>
          </Typography>
          <TextField
            id='budget-request'
            sx={{ mt: '.75em', width: '250px' }}
            variant='filled'
            onChange={handleFieldUpdate}
            label={'Enter Amount'}
            data-testid='actualBudgetAmount'
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            name='actualAmount'
            required={formValidators.actualAmount.required}
            error={submitted && !formValidators.actualAmount.isValid}
            value={formValues.actualAmount ?? ''}
          />
        </Box>
      </>
    )
  }

  return (
    <Modal
      title='ISD/District Program Enhancements Tracking'
      open={props.open}
      data-testid='isdDistrictProgramEnhancementsTrackingModal'
      width='480px'
      confirmButtonText='Save'
      cancelButtonText='Cancel'
      onClose={props.onClose}
      onConfirm={handleSubmit}
      bodyContent={<Stack gap='1rem' m='24px 0'>
        {renderTrackingFields()}
      </Stack>}
    />
  )
}
